import { Card, BlockStack, Text, Box, InlineStack, Icon } from '@shopify/polaris';
import { SearchIcon, ChatIcon, TargetIcon } from '@shopify/polaris-icons';
import { AccentBorderCard, StaggeredList } from '../ui';

/**
 * T019: WhyAiStep — Step 2 of the onboarding wizard.
 * Explains AI discoverability vs traditional SEO without alarmist language.
 * Uses accent border cards with staggered entrance animation.
 *
 * @see documents/ui-enhancement-spec.md §12.2 (Step 2: Why AI)
 */
interface WhyAiStepProps {
  onNext: () => void;
  onBack: () => void;
}

const CONCEPTS = [
  {
    icon: SearchIcon,
    title: 'AI shopping is growing fast',
    description:
      'More shoppers are using AI assistants like ChatGPT, Perplexity, and Google Gemini to discover and compare products before buying.',
  },
  {
    icon: ChatIcon,
    title: 'AI reads your product data differently',
    description:
      'Unlike traditional search engines that match keywords, AI models interpret your titles, descriptions, images, and structured data to understand what you sell.',
  },
  {
    icon: TargetIcon,
    title: 'Small improvements make a big difference',
    description:
      'Better product data means AI assistants are more likely to recommend your products — leading to more traffic and sales from this new channel.',
  },
] as const;

export function WhyAiStep({ onNext: _onNext, onBack: _onBack }: Readonly<WhyAiStepProps>) {
  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingLg">
            Why AI Discoverability matters
          </Text>

          <Text as="p" variant="bodyMd">
            The way shoppers find products is changing. Here&apos;s what you need to know.
          </Text>
        </BlockStack>
      </Card>

      <StaggeredList staggerDelay={0.06} baseDelay={100}>
        {CONCEPTS.map((concept) => (
          <AccentBorderCard key={concept.title}>
            <InlineStack gap="300" align="start" blockAlign="start" wrap={false}>
              <Box
                background="bg-surface-secondary"
                padding="200"
                borderRadius="200"
              >
                <div style={{ background: 'var(--z-accent-muted)', borderRadius: 'var(--p-border-radius-200)', padding: '4px' }}>
                  <Icon source={concept.icon} tone="base" />
                </div>
              </Box>
              <BlockStack gap="100">
                <Text as="h3" variant="headingSm" fontWeight="semibold">
                  {concept.title}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {concept.description}
                </Text>
              </BlockStack>
            </InlineStack>
          </AccentBorderCard>
        ))}
      </StaggeredList>
    </BlockStack>
  );
}
